import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, UploadCloud, MessageSquare, Send, Copy, Download, X, Lock, Star } from 'lucide-react';
import { apiCall, getErrorMessage } from './utils/api';

const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;
  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n ${exp.description}\n`;
  });
  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n ${edu.description}\n`;
  });
  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n ${proj.description}\n`;
  });
  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string' ? data.skills.split(',').map(s => s.trim()).filter(Boolean) : []);
    text += skills.join(', ');
  }
  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => {
    text += `- ${ach.description}\n`;
  });
  text += "\n## Awards\n";
  (data.awards || []).forEach(award => {
    text += `- ${award.description}\n`;
  });
  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => {
    text += `- ${cert.description}\n`;
  });
  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => {
    text += `- ${hobby.description}\n`;
  });
  return text;
};


export default function Playground({ resumeData, userData, user }) {
  // All hooks must be declared first (Rules of Hooks)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [role, setRole] = useState(userData?.role || 'basic');


  const [jobDescription, setJobDescription] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [jobAnalysis, setJobAnalysis] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [questions, setQuestions] = useState([]); // array of strings
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(() => {
    try { return localStorage.getItem('playgroundIntroDismissed') !== '1'; } catch { return true; }
  });
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const resumeText = resumeData ? formatResumeDataAsText(resumeData) : "";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [chatHistory]);

useEffect(() => {
  if (!userData) return;

  const expiresAt = userData.subscription?.expiresAt;
  const currentRole = userData.role;

  if (!expiresAt || currentRole !== 'premium') {
    setRole(currentRole || 'basic');
    return;
  }

  // Handle both Firestore Timestamp and JS Date strings
  let expiryDate;
  if (expiresAt && typeof expiresAt.toDate === 'function') {
    expiryDate = expiresAt.toDate(); // Standard Firestore Timestamp
  } else if (expiresAt?.seconds) {
    expiryDate = new Date(expiresAt.seconds * 1000); // Plain object timestamp
  } else {
    expiryDate = new Date(expiresAt); // ISO String
  }

  if (new Date() > expiryDate) {
    console.log("Frontend detected expiry - restricting view");
    setRole('basic'); 
  } else {
    setRole(currentRole);
  }
}, [userData]);


  // Check if user is premium, if not show upgrade modal and block access
  // if (role !== 'premium') {
  //   return (
  //     <div>
  //       <h2 className="text-3xl font-bold text-black-500 mb-8">Interview Copilot</h2>
  //       <div className="flex flex-col items-center justify-center min-h-[400px] bg-[#fafafa] rounded-xl border border-[#d5e7f3] p-8">
  //         <Lock size={64} className="text-[#3C0F6C] mb-4" />
  //         <h3 className="text-2xl font-bold text-[#3C0F6C] mb-2">Premium Feature</h3>
  //         <p className="text-[#3C0F6C]/80 mb-6 text-center max-w-md">
  //           This feature is available for premium users only. Upgrade your account to access Interview Copilot.
  //         </p>
  //         <button
  //           onClick={() => setIsUpgradeModalOpen(true)}
  //           className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-6 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors"
  //         >
  //           Upgrade to Premium
  //         </button>
  //       </div>
  //       {isUpgradeModalOpen && (
  //         <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
  //           <div className="bg-gray-900 border border-yellow-500/30 rounded-xl shadow-xl max-w-md w-full text-center p-8">
  //             <div className="w-16 h-16 bg-yellow-500/10 border-4 border-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
  //               <Star className="text-yellow-400" size={32} />
  //             </div>
  //             <h3 className="text-2xl font-bold text-white mt-4">Upgrade to Premium</h3>
  //             <p className="text-gray-400 mt-2">This is a premium feature. Upgrade your account to unlock advanced AI analysis and personalized career counseling.</p>
  //             <div className="mt-6 flex gap-4">
  //               <button onClick={() => setIsUpgradeModalOpen(false)} className="w-full bg-gray-700 text-white font-semibold py-2.5 rounded-lg hover:bg-gray-600 transition-colors">Maybe Later</button>
  //               <button className="w-full bg-green-500 text-gray-900 font-semibold py-2.5 rounded-lg hover:bg-green-600 transition-colors">Upgrade Now</button>
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // }



  const handleClearChat = () => {
    setChatHistory([]);
    setChatInput('');
  };

  const handleShowGuide = () => {
    setShowIntro(true);
    try { localStorage.removeItem('playgroundIntroDismissed'); } catch (err) {
      console.error('Error updating localStorage', err);
    }
  };

  const handleJobPdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null);
    try {
      const formData = new FormData();
      // Reuse existing analyzer to extract text; backend returns { text }
      formData.append('resume_file', file);
      const { data } = await apiCall('/api/analyze_resume', {
        method: 'POST',
        body: formData
      });
      const extracted = data.text || '';
      setJobDescription(extracted);
    } catch (err) {
      console.error('JD analyze error:', err);
      setError(err.message || 'Failed to analyze job description PDF.');
    } finally {
      // clear file input value so same file can be re-uploaded if desired
      e.target.value = '';
    }
  };

  // Lightweight client-side analyzer for pasted job description
  const analyzeJobText = (text) => {
    const t = (text || '').toString();
    const lower = t.toLowerCase();
    // Extract skills from a common tech dictionary
    const techDict = [
      'javascript', 'typescript', 'python', 'java', 'c++', 'c#', 'go', 'rust', 'ruby', 'react', 'angular', 'vue', 'node', 'node.js', 'django', 'flask', 'spring', 'rails', 'aws', 'gcp', 'azure', 'docker', 'kubernetes', 'sql', 'postgres', 'mysql', 'mongodb', 'nosql', 'spark', 'hadoop', 'tensorflow', 'pytorch', 'nlp', 'llm', 'ml', 'ai', 'ci/cd', 'git', 'linux', 'terraform'
    ];
    const foundSkills = Array.from(new Set(
      techDict.filter(k => lower.includes(k)).map(s => s.toUpperCase())
    ));

    // Experience level and years
    const yearsMatch = t.match(/(\d+)[+]?\s*(?:years|yrs)/i);
    const years = yearsMatch ? yearsMatch[1] : null;
    const seniority = (/senior|lead|principal/i.test(t) ? 'Senior' : (/junior|entry/i.test(t) ? 'Junior' : (/staff/i.test(t) ? 'Staff' : 'Mid')));

    // Company expectations (simple heuristics)
    const expectationKeywords = ['ownership', 'collaborat', 'deliver', 'scale', 'design', 'mentor', 'best practices', 'stakeholder', 'cross-functional', 'communication'];
    const expectations = expectationKeywords
      .filter(k => lower.includes(k))
      .map(k => k.replace(/\b\w/g, c => c.toUpperCase()));

    // Relevant keywords (top frequent nouns-like words)
    const tokens = lower.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
    const stop = new Set(['the', 'and', 'for', 'with', 'you', 'your', 'our', 'to', 'of', 'a', 'in', 'on', 'as', 'is', 'are', 'be', 'or', 'we', 'will', 'an']);
    const freq = {};
    for (const w of tokens) {
      if (w.length < 3 || stop.has(w)) continue;
      freq[w] = (freq[w] || 0) + 1;
    }
    const keywords = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([w]) => w);

    return {
      required_skills: foundSkills,
      experience_years: years,
      experience_level: seniority,
      company_expectations: expectations,
      keywords,
    };
  };



  const handleAnalyzeJob = async () => {
    setSubmitMessage('');
    if (!resumeText) {
      setError('Please ensure your resume data is loaded.');
      return;
    }
    const url = (jobUrl || '').trim();
    let input = (jobDescription || '').trim();
    if (!url && !input) {
      setError('Please provide a Job URL or paste the job description.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // If URL is provided, fetch description from backend
      if (url) {
        const { data } = await apiCall('/api/analyze_job_url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url })
        });
        const extracted = (data.job_description || '').trim();
        if (extracted) {
          input = extracted; // prefer extracted description
          setJobDescription(extracted);
        }
      }

      if (!input) {
        throw new Error('No job description found from the provided URL. Please paste the description manually.');
      }

      const insights = analyzeJobText(input);
      setJobAnalysis(insights);
      setSubmitMessage('Resume and job description processed. You can now ask the chatbot about the job.');
    } catch (err) {
      console.error('Error processing job description:', err);
      setError(err.message || 'Failed to process job description.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuestions = async () => {
    const resumeForContext = resumeText;
    if (!resumeForContext || !jobDescription) {
      setError('Please upload and analyze a resume and analyze a job description.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await apiCall('/api/generate_questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resume: resumeForContext, job: jobDescription })
      });

      const qs = Array.isArray(data.questions) ? data.questions.filter(Boolean).map(String) : [];
      setQuestions(qs);
    } catch (err) {
      console.error('Error generating questions:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    const resumeForContext = resumeText;
    if (!chatInput.trim() || isChatLoading) return;

    const userMessage = chatInput;
    const newHistory = [...chatHistory, { role: 'user', content: userMessage }];
    setChatHistory(newHistory);
    setChatInput('');
    setIsChatLoading(true);

    try {
      const { data } = await apiCall('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          history: chatHistory,
          message: userMessage,
          context: {
            resume_text: resumeForContext,
            job_description: jobDescription,
            questions: questions,
            job_analysis: jobAnalysis,
          }
        })
      });

      const aiResponse = data.reply || data.response || "I'm sorry, I couldn't process that. Please try again.";
      setChatHistory(prevHistory => [...prevHistory, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('Chatbot API error:', error);
      setChatHistory(prevHistory => [...prevHistory, { role: 'assistant', content: 'An error occurred. Please try again.' }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleCopyQuestions = async () => {
    try {
      const text = questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
      await navigator.clipboard.writeText(text);
    } catch (err) {

      console.error('Failed to copy questions:', err);
      // no-op
    }
  };

  const handleDownloadQuestions = () => {
    try {
      const text = questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'interview_questions.txt';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download questions:', err);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Dynamic Main Content & Chat Column */}
      <h2 className="text-3xl font-bold text-black-500 mb-8">Interview Copilot</h2>
      {showIntro && (
        <div className="mb-4 bg-[#ede7f6] border border-[#d5e7f3] rounded-xl p-4 text-[#3C0F6C]">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="font-semibold">What this page does</p>
              <p className="text-sm">Practice interview prep with AI using your resume and a job description. Ask questions, get tailored guidance, and iterate on answers.</p>
              <p className="font-semibold mt-2">How to use it</p>
              <ul className="list-decimal pl-5 text-sm space-y-1">
                <li>Paste or upload a job description and press Submit to analyze.</li>
                <li>Use the chat on the left to ask for tips, drills, or feedback.</li>
              </ul>
            </div>
            <button
              onClick={() => {
                setShowIntro(false); try { localStorage.setItem('playgroundIntroDismissed', '1'); } catch (err) {
                  console.error('Failed to update localStorage', err);
                }
              }}
              className="shrink-0 text-sm px-3 py-1 rounded-lg border bg-[#FFD600] border-[#FFD600] text-[#0E0E0E] hover:bg-[#e6c900]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-row w-full space-x-4">
        <div className="flex-1 bg-[#fafafa] p-6 rounded-xl shadow-sm border border-[#d5e7f3] flex flex-col">
          <header className="mb-4">
            <p className="text-[#3C0F6C]/80 mt-1">
              A flexible testing ground for interview prep—experiment with AI models and iterate on responses.
            </p>
          </header>

          {/* Only show Chatbot view, no tab navigation or AI Interview Questions */}
          <div className="flex-1 flex flex-col">
            <div className="mb-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleClearChat}
                className="px-3 py-2 text-sm rounded-xl border border-[#d5e7f3] text-[#3C0F6C] bg-white hover:bg-[#f3f0fa]"
              >
                Clear Chat
              </button>
              <button
                type="button"
                onClick={handleShowGuide}
                title="Show page instructions"
                className="px-3 py-2 text-sm rounded-xl border border-[#d5e7f3] text-[#3C0F6C] bg-white hover:bg-[#f3f0fa]"
              >
                Help?
              </button>
            </div>
            <div className="flex-1 p-4 pb-24 space-y-4 bg-[#d5e7f3]/40 rounded-xl">
              <div className="text-center text-sm text-[#3C0F6C]/60 italic">
                <p>Ask me anything about your resume or career path.</p>
              </div>
              {chatHistory.map((msg, index) => (
                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`p-4 rounded-xl max-w-[50%] whitespace-pre-wrap ${msg.role === 'user' ? 'bg-[#3C0F6C] text-[#fafafa] rounded-br-none' : 'bg-[#fafafa] text-[#3C0F6C] rounded-bl-none'
                    }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isChatLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#d5e7f3] text-[#3C0F6C] p-3 rounded-xl rounded-bl-none max-w-[80%]">
                    <svg className="animate-spin h-5 w-5 text-[#3C0F6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendChat} className="mt-4 flex items-center gap-2 sticky bottom-0 bg-[#fafafa] pt-2 z-10">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask about your resume..."
                className="flex-1 px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
                disabled={isChatLoading || !resumeText}
              />
              <button
                type="submit"
                className="bg-[#3C0F6C] text-[#fafafa] font-semibold p-3 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isChatLoading || !chatInput.trim() || !resumeText}
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar - Job Analysis */}
        <div className="w-1/3 bg-[#fafafa] p-6 rounded-xl shadow-sm border border-[#d5e7f3]">
          <h3 className="text-xl font-bold mb-4 text-[#3C0F6C]">Job Analysis</h3>



          {/* Job Description Section */}
          <div className="mb-4">
            <label className="text-sm font-semibold mb-2 block text-[#3C0F6C]/80">Job URL (optional)</label>
            <input
              type="url"
              value={jobUrl}
              onChange={(e) => setJobUrl(e.target.value)}
              placeholder="Paste job posting URL here..."
              className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
            />
            <div className="h-2" />
            <label className="text-sm font-semibold mb-2 block text-[#3C0F6C]/80">Job Description</label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste job description here..."
              className="w-full px-4 py-3 h-40 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50"
            />
            <input
              id="jd-upload"
              type="file"
              accept=".pdf,.docx"
              onChange={handleJobPdfUpload}
              className="hidden"
            />
            <label htmlFor="jd-upload" className="mt-2 block w-full text-center px-4 py-2 text-sm font-semibold rounded-xl cursor-pointer bg-[#d5e7f3] text-[#3C0F6C] hover:bg-[#d5e7f3]/80 transition-colors">
              Upload Job Description (PDF/DOCX)
            </label>
            <button
              onClick={handleAnalyzeJob}
              disabled={isLoading || !resumeText || !(jobUrl.trim() || jobDescription.trim())}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-4 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>
            {submitMessage && (
              <div className="mt-3 text-sm bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg">
                {submitMessage}
              </div>
            )}
            {error && (
              <div className="mt-3 text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}